import * as TOML from "@ltd/j-toml";
import {
  AvailableQuestionType,
  guessQuestionTypeByName,
  questionTypeSpecs,
} from "./question-type-specs";

/**
 * The server loads question `data` with `Jason.decode(keys: :atoms!)`
 * (`Haven.Domain.Forms.QuestionData.load/1`), so every key under `data` has to
 * already exist as an atom on the backend. A key it does not define -- a camelCase
 * GraphQL name such as `yesText`, or a typo -- is not rejected by Ecto on the way in.
 * It saves cleanly and only surfaces once the draft is loaded, as a 500 from
 * `binary_to_existing_atom/2` for every applicant who opens the form. Catch it here.
 */
const SNAKE_CASE_KEY = /^[a-z][a-z0-9_]*$/u;

type Questions = Record<string, string>;

const toSnakeCase = (key: string): string =>
  key.replace(/(?<lower>[a-z0-9])(?<upper>[A-Z])/gu, "$<lower>_$<upper>").toLowerCase();

const isPlainObject = (value: unknown): value is Record<string, unknown> =>
  typeof value === "object" && value !== null && !Array.isArray(value);

const collectKeyPaths = (value: unknown, path: string[], into: string[][]): void => {
  if (Array.isArray(value)) {
    value.forEach((item) => collectKeyPaths(item, path, into));
    return;
  }
  if (!isPlainObject(value)) return;

  Object.keys(value).forEach((key) => {
    const nested = path.concat(key);
    into.push(nested);
    collectKeyPaths(value[key], nested, into);
  });
};

const isPrefix = (prefix: string[], path: string[]): boolean =>
  prefix.length <= path.length && prefix.every((part, index) => path[index] === part);

type DeclaredFields = { requiredDataFields: string[], optionalDataFields: string[] };

const declaredFieldsOf = (typeSpec: DeclaredFields): string[] =>
  typeSpec.requiredDataFields.concat(typeSpec.optionalDataFields);

/**
 * Every data field any question type declares. The backend's atom table is global,
 * so a key is loadable if some type defines it -- `residence_reason_moving` is
 * registered as `text` but ships `data.options`, and that has to keep saving.
 */
const KNOWN_DATA_FIELDS = Array.from(
  new Set(Object.values(questionTypeSpecs).flatMap(declaredFieldsOf)),
);

/**
 * Loadable and allowed on every type so they never block a save, but ignored by the
 * renderer. Live drafts set `required` under `data` instead of on the question.
 */
const MISPLACED_DATA_KEYS: Record<string, string> = {
  required: "It is ignored here -- set required on the question itself.",
};

const KNOWN_DATA_PATHS = KNOWN_DATA_FIELDS
  .concat(Object.keys(MISPLACED_DATA_KEYS).map((key) => `data.${key}`))
  .map((field) => field.split("."));

/**
 * A declared field is a leaf: anything below it, such as the option objects inside
 * `data.options`, is opaque and only snake_case-checked. Any other key has to be a
 * declared field or on the way to one (`data.custom` for `data.custom.yes_text`), so
 * a typo like `data.placehodler` or `data.custom.yes_txt` is reported at any depth.
 */
const isUnknownPath = (path: string[]): boolean =>
  !KNOWN_DATA_PATHS.some((field) => isPrefix(field, path) || isPrefix(path, field));

const allowedFieldsHint = (questionType: string | null): string => {
  const typeSpec = questionTypeSpecs[questionType as AvailableQuestionType];
  const typeFields = typeSpec ? declaredFieldsOf(typeSpec) : [];

  if (typeFields.length > 0) return `Allowed for type ${questionType}: ${typeFields.join(", ")}.`;

  return `Known data keys: ${KNOWN_DATA_FIELDS.join(", ")}.`;
};

export const questionToQuestionString = (question: string[]): string =>
  `["${question.join("\", \"")}"]`;

/**
 * Needs no question type: both checks hold for every type, so a question whose
 * type is not resolved yet (FormQuestions still loading, a name no type prefix
 * matches) is still gated. The type only narrows the hint in the message.
 */
export const validateQuestionDataKeys = (
  question: Record<string, any>,
  questionType: string | null,
  questionString: string,
): string[] => {
  if (!isPlainObject(question.data)) return [];

  const paths: string[][] = [];
  collectKeyPaths(question.data, ["data"], paths);

  return paths.flatMap((path) => {
    const key = path[path.length - 1];
    const dotted = path.join(".");

    if (!SNAKE_CASE_KEY.test(key)) {
      const suggestion = path.slice(0, -1).concat(toSnakeCase(key)).join(".");
      const reason = "the server loads data keys as atoms and rejects names it does not define";
      const fix = `Use snake_case "${suggestion}" -- ${reason}.`;
      return [`${questionString} has invalid data key "${dotted}". ${fix}`];
    }

    if (isUnknownPath(path)) {
      const hint = allowedFieldsHint(questionType);
      return [`${questionString} has unknown data key "${dotted}". ${hint}`];
    }

    return [];
  });
};

const validateQuestionDataKeyWarnings = (
  question: Record<string, any>,
  questionString: string,
): string[] => {
  if (!isPlainObject(question.data)) return [];
  const { data } = question;

  return Object.entries(MISPLACED_DATA_KEYS)
    .filter(([key]) => key in data)
    .map(([key, fix]) => `${questionString} has misplaced data key "data.${key}". ${fix}`);
};

export const questionNamePartsOf = (question: Record<string, any>): string[] =>
  (Array.isArray(question.name) ? question.name : [])
    .map((value: unknown) => `${value}`.trim())
    .filter(Boolean);

export const questionTypeOf = (
  questionName: string,
  existingQuestions: Questions,
): string | null => existingQuestions[questionName] || guessQuestionTypeByName(questionName);

/**
 * The subset of draft validation that has to gate saving, not just the Validate
 * button. An unresolvable data key is never a legitimate work-in-progress state the
 * way an unlinked question or a half-built layout is. TOML that does not parse
 * reports nothing here -- the save paths already handle that.
 */
export const findInvalidQuestionDataKeys = (
  content: string | undefined,
  existingQuestions: Questions,
): string[] => {
  let questionsList: Record<string, any>[] = [];

  try {
    questionsList = (TOML.parse[0.5](content || "", "\n").question || []) as Record<string, any>[];
  } catch {
    return [];
  }

  return questionsList.flatMap((question) => {
    const questionNameParts = questionNamePartsOf(question);
    const [questionName] = questionNameParts;
    if (!questionName) return [];

    return validateQuestionDataKeys(
      question,
      questionTypeOf(questionName, existingQuestions),
      questionToQuestionString(questionNameParts),
    );
  });
};

/** Advisory only: never gates Save or Publish. */
export const findQuestionDataKeyWarnings = (content: string | undefined): string[] => {
  let questionsList: Record<string, any>[] = [];

  try {
    questionsList = (TOML.parse[0.5](content || "", "\n").question || []) as Record<string, any>[];
  } catch {
    return [];
  }

  return questionsList.flatMap((question) => validateQuestionDataKeyWarnings(
    question,
    questionToQuestionString(questionNamePartsOf(question)),
  ));
};
